import { DateGroup as EDateGroup } from '@starsoft/common/enums/dateGroup';
import styles from './styles.module.scss';
import { Fragment, useMemo } from 'react';
import { DateGroupProps } from './props';
import { useTranslation } from '@starsoft/common/hooks';

/**
 * Component that renders a group of date range selection buttons
 * Provides quick selection options for common date ranges like today, last 15 days, and last 30 days
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Callback function called when a date range is selected
 * @param {boolean} [props.isDefault] - Whether this is the default date group
 * @returns {JSX.Element} Date group buttons with dividers
 */
export default function DateGroup({ onChange, isDefault }: DateGroupProps) {
  const { t } = useTranslation('common');

  /**
   * Gets the start and end dates for a given date group option
   *
   * @param {EDateGroup} group - The date group enum value
   * @returns {[Date, Date]} Array containing start and end dates
   */
  function getDateRange(group: EDateGroup): [Date, Date] {
    switch (group) {
      case EDateGroup.TODAY: {
        const today: Date = new Date();
        return [today, today];
      }
      case EDateGroup.FIFTEEN_DAYS: {
        const endDate: Date = new Date();
        const startDate: Date = new Date();
        startDate.setDate(endDate.getDate() - 15);
        return [startDate, endDate];
      }
      case EDateGroup.THIRTY_DAYS: {
        const endDate: Date = new Date();
        const startDate: Date = new Date();
        startDate.setDate(endDate.getDate() - 30);
        return [startDate, endDate];
      }
    }
  }

  /**
   * Memoized array of date group buttons with dividers between them
   * Each button triggers the onChange callback with its corresponding date range when clicked
   */
  const Buttons: JSX.Element[] = useMemo(
    () =>
      [EDateGroup.TODAY, EDateGroup.FIFTEEN_DAYS, EDateGroup.THIRTY_DAYS].map(
        (dateGroup, i, array) => (
          <Fragment key={`date_group_${dateGroup}`}>
            <button
              className={styles.container__button}
              onClick={() => onChange(getDateRange(dateGroup as EDateGroup))}
            >
              {t(`date.${dateGroup}`)}
            </button>

            {i !== array.length - 1 && (
              <div className={styles.container__divider} />
            )}
          </Fragment>
        ),
      ),
    //eslint-disable-next-line
    [t],
  );

  return (
    <div
      className={`${styles.container} ${isDefault ? styles['container--default'] : ''}`}
    >
      {Buttons}
    </div>
  );
}
